import type { NodeRegistrationStatus, NodeRegistryView } from '@/apps/apis/commandCenter/types';
import { sortForApproval } from '../domain/nodeRegistry';

/** Pending is a queue state, not a failure, so it never gets the critical colour. */
const STATUS_LABEL: Record<NodeRegistrationStatus, string> = {
  pending: 'awaiting approval',
  approved: 'approved',
  revoked: 'revoked',
};

const STATUS_TONE: Record<NodeRegistrationStatus, string> = {
  pending: 'b-warn',
  approved: 'b-ok',
  revoked: 'b-crit',
};

const formatSeen = (iso: string): string => {
  const parsed = Date.parse(iso);
  return Number.isNaN(parsed) ? 'unknown' : new Date(parsed).toISOString().replace('T', ' ').slice(0, 16);
};

interface NodeRegistryPanelProps {
  registry: NodeRegistryView | undefined;
  isError: boolean;
}

/** Read-only node roster. Node enrollment and lifecycle changes happen outside Command Center. */
const NodeRegistryPanel = ({ registry, isError }: NodeRegistryPanelProps) => {
  const nodes = sortForApproval(registry?.nodes ?? []);

  return (
    <div className="panel" data-testid="node-registry-panel">
      <div className="panel-h">
        <span className="t">Nodes</span>
        <span className="sub">
          {isError
            ? 'registry unavailable'
            : `${nodes.length} registered · ${registry?.pending ?? 0} awaiting approval`}
        </span>
      </div>

      {isError ? (
        <div className="panel-b crit-text">Node registry unavailable — the board above is unaffected.</div>
      ) : nodes.length === 0 ? (
        <div className="panel-b no-links">No nodes registered yet.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Host</th>
              <th>Node</th>
              <th>Status</th>
              <th>Labels</th>
              <th>Last seen</th>
            </tr>
          </thead>
          <tbody>
            {nodes.map((node) => (
              <tr key={node.nodeId} data-testid={`node-row-${node.nodeId}`} data-status={node.status}>
                <td>
                  {node.displayName !== null ? (
                    <>
                      <span>{node.displayName}</span>
                      {node.hostname !== null ? <span className="muted-cell"> {node.hostname}</span> : null}
                    </>
                  ) : (
                    (node.hostname ?? <span className="muted-cell">hostname unknown</span>)
                  )}
                </td>
                <td className="mono">{node.nodeId}</td>
                <td>
                  <span className={`a-badge ${STATUS_TONE[node.status]}`}>{STATUS_LABEL[node.status]}</span>
                </td>
                <td>
                  <span className="a-tags">
                    {node.labels.map((label) => (
                      <span key={label} className="tg">
                        {label}
                      </span>
                    ))}
                  </span>
                </td>
                <td className="muted-cell">{formatSeen(node.lastSeen)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default NodeRegistryPanel;
